import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button as BlueprintButton, Icon, Intent } from '@blueprintjs/core';
import { FormDateInput, FormHelperText, FormSwitch, FormTextArea } from 'components/form-fields';
import { Money, Numeric } from 'components/formatting';
import { GridLayout } from 'components/grid';
import { ShowHide } from 'components/layout';
import { ListingTable } from 'components/listing';
import { NotificationInline } from 'components/notifications';
import {
    buildFactText,
    buildFactValue,
    findTachoItemField,
    getTachoItemStatus,
    getTachoVisibleItems,
    tachoItemStatuses,
    tachoItemsPageSize,
    toApiDate
} from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';
import { TachoDebriefDialog } from './TachoDebriefDialog';
import './CompleteReviewSteps.css';

const defaultArray = [];
const defaultObject = {};
const noItemsMessage = "There are no outstanding infringements for this driver.";
const allItemsClearedMessage = "Every outstanding infringement occurred before this date and will be cleared.";
const olderItemsMayBeClearedMessage = "Infringements older than those listed may also be cleared.";
const occurredAtFormat = "DD/MM/YYYY HH:mm";

const headers = [
    "Code",
    "Date",
    "Infringement",
    "GFP cost",
    "Action"
];

// The tick carries the success colour itself, so the button stays as plain as the speech bubble.
const reviewedIcon = <Icon icon="tick" intent={Intent.SUCCESS} />;

const statusRowClasses = Object.freeze({
    [tachoItemStatuses.reviewed]: "tacho-infringement-reviewed-row"
});

// An infringement can have occurred long before the review, so the clear date reaches far back.
// Held at module scope because FormDateInput keys an effect off the bound references.
const earliestClearDate = moment("1900-01-01").toDate();

// Codes repeat across infringements, so each debrief button is named by its code and the time
// it occurred, formatted as the table shows it. A reviewed infringement's button still opens its
// debrief, so its name says the debrief is there to change.
function buildDebriefLabel(item, status) {
    const action = status === tachoItemStatuses.reviewed ? "Edit debrief" : "Debrief";
    const code = buildFactText(item, "Code");
    const occurredAt = findTachoItemField(item, "OccurredAt")?.value;
    const occurredAtMoment = occurredAt == null || occurredAt === "" ? null : moment.utc(occurredAt);

    if (occurredAtMoment == null || !occurredAtMoment.isValid()) {
        return `${action} ${code}`;
    }

    return `${action} ${code} ${occurredAtMoment.local().format(occurredAtFormat)}`;
}

// The Tacho Infringements step of the complete-review wizard: the driver's outstanding
// infringements, each of which the manager can debrief, alongside the choices that settle the
// rest at completion - clearing items before a date, or marking them all as reviewed. Nothing
// is sent until the review is completed.
export function TachoInfringementsStep(
    {
        state = null,
        loading = false,
        onChange
    }
) {

    const items = state?.items ?? defaultArray;
    const records = state?.records ?? defaultObject;
    const debrief = state?.debrief ?? null;
    const markAllReviewed = state?.markAllReviewed === true;
    const itemsUnavailableMessage = state?.itemsUnavailableMessage ?? null;
    const outstandingItemCount = state?.outstandingItemCount ?? 0;
    const totalGfpCost = state?.totalGfpCost ?? null;
    const pageSize = state?.pageSize ?? tachoItemsPageSize;

    // Clearing items before a future date would clear infringements that have not happened yet,
    // so the clear date stops at the end of today. Taken when the step mounts and memoised so
    // the reference FormDateInput keys its effect off stays stable across renders.
    const latestClearDate = useMemo(() => moment().endOf("day").toDate(), []);

    const debriefItem = items.find((item) => item.itemReference === debrief?.itemReference) ?? null;
    const debriefHasRecord = debrief != null && Object.hasOwn(records, debrief.itemReference);

    function onPagingChange(newPageNumber, newPageSize) {
        onChange({ pageNumber: newPageNumber, pageSize: newPageSize });
    }

    function onClearItemsBeforeChange(value) {
        onChange({ clearItemsBefore: toApiDate(value) });
    }

    function onMarkAllReviewedChange(event) {
        onChange({ markAllReviewed: event.target.checked });
    }

    function onOpenDebrief(item) {
        const record = records[item.itemReference];

        onChange({
            editing: true,
            debrief: {
                itemReference: item.itemReference,
                reasonForFault: record?.reasonForFault ?? "",
                actionTaken: record?.actionTaken ?? "",
                emergency: record?.emergency === true
            }
        });
    }

    function onDebriefFieldChange(fieldName, value) {
        onChange((topicState) => ({ debrief: { ...topicState.debrief, [fieldName]: value } }));
    }

    function onDebriefCancel() {
        onChange({ debrief: null, editing: false });
    }

    function onDebriefSave() {
        onChange((topicState) => ({
            records: {
                ...topicState.records,
                [debrief.itemReference]: {
                    reasonForFault: debrief.reasonForFault,
                    actionTaken: debrief.actionTaken,
                    emergency: debrief.emergency
                }
            },
            debrief: null,
            editing: false
        }));
    }

    function onDebriefRemove() {
        onChange((topicState) => {
            const remainingRecords = { ...topicState.records };
            delete remainingRecords[debrief.itemReference];

            return { records: remainingRecords, debrief: null, editing: false };
        });
    }

    function onCommentChange(event) {
        onChange({ comment: event.target.value });
    }

    const clearItemsBefore = state?.clearItemsBefore ?? null;
    const visibleItems = useMemo(
        () => getTachoVisibleItems({ items, clearItemsBefore }),
        [items, clearItemsBefore]
    );

    // Only the most recent infringements are listed, so once the clear date reaches a listed one
    // every unlisted, older infringement is cleared too, and the notice that the list is capped
    // no longer applies. While it reaches none of them, older unlisted ones may still be cleared,
    // but how many is not known.
    const isListCapped = outstandingItemCount > items.length;
    const clearedListedItemCount = items.length - visibleItems.length;
    const showCappedListNotice = isListCapped && clearedListedItemCount === 0;
    const clearedItemCount = isListCapped && clearedListedItemCount > 0
        ? clearedListedItemCount + (outstandingItemCount - items.length)
        : clearedListedItemCount;
    const olderItemsMayBeCleared = isListCapped && clearItemsBefore != null && clearedListedItemCount === 0;

    // The table re-seats itself on startPage whenever its row count moves, so the page is driven
    // from here and clamped to the pages the listed items fill.
    const totalPages = Math.max(1, Math.ceil(visibleItems.length / pageSize));
    const activePage = Math.min(state?.pageNumber ?? 1, totalPages);
    const pageStart = (activePage - 1) * pageSize;
    const pageItems = visibleItems.slice(pageStart, pageStart + pageSize);
    const pageStatuses = pageItems.map((item) => getTachoItemStatus(state, item));

    const rowClasses = pageStatuses.map((status) => statusRowClasses[status] ?? null);

    const rowData = pageItems.map((item, index) => {
        const reference = item.itemReference;
        const status = pageStatuses[index];
        const isReviewed = status === tachoItemStatuses.reviewed;

        return [
            (
                <span key={`tacho-infringement-code-${reference}`} className="nowrap">{buildFactValue(item, "Code")}</span>
            ),
            (
                <Fragment key={`tacho-infringement-occurred-${reference}`}>{buildFactValue(item, "OccurredAt")}</Fragment>
            ),
            (
                <Fragment key={`tacho-infringement-type-${reference}`}>{buildFactValue(item, "InfringementType")}</Fragment>
            ),
            (
                <span key={`tacho-infringement-gfp-cost-${reference}`} className="nowrap">{buildFactValue(item, "GfpCost")}</span>
            ),
            (
                <BlueprintButton
                    key={`tacho-infringement-debrief-${reference}`}
                    id={`tacho-infringement-debrief-${reference}`}
                    icon={isReviewed ? reviewedIcon : "chat"}
                    variant="minimal"
                    intent={isReviewed ? Intent.NONE : Intent.PRIMARY}
                    aria-label={buildDebriefLabel(item, status)}
                    onClick={() => onOpenDebrief(item)}
                />
            )
        ];
    });

    const outstandingItemsNoun = outstandingItemCount === 1 ? "infringement" : "infringements";

    const clearedItemCountText = clearedItemCount === 1
        ? "1 infringement before this date will be cleared."
        : `${clearedItemCount} infringements before this date will be cleared.`;

    return (
        <div>
            <h2>Tacho Infringements</h2>
            <FormHelperText loading={loading}>Debrief the driver on each outstanding infringement.</FormHelperText>
            <ShowHide
                evaluator={itemsUnavailableMessage != null}
                show={(
                    <NotificationInline
                        allowClose={false}
                        show
                        text={itemsUnavailableMessage}
                        intent="primary"
                    />
                )}
                hide={(
                    <ShowHide
                        evaluator={!loading && items.length === 0}
                        show={(
                            <p>{noItemsMessage}</p>
                        )}
                        hide={(
                            <Fragment>
                                <ShowHide
                                    evaluator={items.length > 0}
                                    show={(
                                        <p id="tacho-infringements-summary">
                                            <Numeric>{outstandingItemCount}</Numeric> {outstandingItemsNoun} outstanding
                                            <ShowHide
                                                evaluator={totalGfpCost != null}
                                                show={(
                                                    <Fragment> · Total GFP cost <Money>{totalGfpCost}</Money></Fragment>
                                                )}
                                            />
                                        </p>
                                    )}
                                />
                                <ShowHide
                                    evaluator={showCappedListNotice}
                                    show={(
                                        <NotificationInline
                                            allowClose={false}
                                            show
                                            text={(
                                                <Fragment>
                                                    Showing the <Numeric>{items.length}</Numeric> most recent of <Numeric>{outstandingItemCount}</Numeric> outstanding infringements. Clear items before a date to remove older ones.
                                                </Fragment>
                                            )}
                                            intent="primary"
                                        />
                                    )}
                                />
                                <GridLayout numberOfColumns={2} gap={[0, 20]}>
                                    <div>
                                        <FormDateInput
                                            id="tacho-infringements-clear-items-before"
                                            headingText="Clear items before"
                                            helperText="Infringements that occurred before this date are cleared when the review is completed."
                                            value={state?.clearItemsBefore ?? null}
                                            minDate={earliestClearDate}
                                            maxDate={latestClearDate}
                                            loading={loading}
                                            onChange={onClearItemsBeforeChange}
                                        />
                                        <ShowHide
                                            evaluator={clearedItemCount > 0 && visibleItems.length > 0}
                                            show={(
                                                <FormHelperText>{clearedItemCountText}</FormHelperText>
                                            )}
                                        />
                                        <ShowHide
                                            evaluator={olderItemsMayBeCleared}
                                            show={(
                                                <FormHelperText>{olderItemsMayBeClearedMessage}</FormHelperText>
                                            )}
                                        />
                                    </div>
                                    <ShowHide
                                        evaluator={state?.allowMarkAllReviewed === true}
                                        show={(
                                            <FormSwitch
                                                id="tacho-infringements-mark-all-reviewed"
                                                headingText="Mark all as reviewed"
                                                helperText="Every infringement not already debriefed is recorded as reviewed."
                                                checked={markAllReviewed}
                                                loading={loading}
                                                onChange={onMarkAllReviewedChange}
                                            />
                                        )}
                                    />
                                </GridLayout>
                                <ShowHide
                                    evaluator={!loading && visibleItems.length === 0}
                                    show={(
                                        <p>{allItemsClearedMessage}</p>
                                    )}
                                    hide={(
                                        <ListingTable
                                            id="tacho-infringement-items"
                                            loadingData={loading}
                                            headers={headers}
                                            data={rowData}
                                            rowClasses={rowClasses}
                                            totalRecordCount={visibleItems.length}
                                            showPerPage={true}
                                            pageSize={pageSize}
                                            startPage={activePage}
                                            onPagingChange={onPagingChange}
                                            rowClickEnabled={false}
                                        />
                                    )}
                                />
                            </Fragment>
                        )}
                    />
                )}
            />
            <div className="spacer-top">
                <FormTextArea
                    id="tacho-infringements-comment"
                    headingText="Overall comments"
                    placeholder="Add any overall comments for this topic..."
                    value={state?.comment ?? ""}
                    loading={loading}
                    fill
                    onChange={onCommentChange}
                />
            </div>
            <TachoDebriefDialog
                item={debriefItem}
                debrief={debrief}
                allowRemove={debriefHasRecord}
                onChange={onDebriefFieldChange}
                onSave={onDebriefSave}
                onCancel={onDebriefCancel}
                onRemove={onDebriefRemove}
            />
        </div>
    );
}

TachoInfringementsStep.propTypes = {
    state: PropTypes.object,
    loading: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};
